import { Constants } from 'utils/constants';
import * as yup from 'yup';
import {
  dateSchema,
  dropdownOptional,
  dropdownRequired,
  nullableNumberSchema,
  nullableStringSchema,
  numberSchemaRequired,
  twoDecimalNullableSchema,
  twoDecimalNumbersSchema,
} from './common';

type BaseSchema = yup.BaseSchema<Date | number | null | undefined>;

const binsPerTarp = 50;

const reqErrorStr = 'At least one data record is required.';

const tarpsToBinsHauledSchema = yup
  .number()
  .nullable()
  .moreThan(0, Constants.errorMessages.POSITIVE_NUMBER_REQUIRED)
  .integer(Constants.errorMessages.INTEGER_REQUIRED)
  .test(
    'more-than-bins-per-tarp',
    'Field is too high considering the number of bins hauled.',
    (val, context) => {
      if (typeof val === 'number') {
        return val - 1 < context.parent.binsHauled / binsPerTarp;
      }
      return true;
    },
  );

const requiredIfBinsExistTest = {
  name: 'required-if-bins-exist',
  message: 'Required if bins hauled entered.',
  testField: 'binsHauled',
};

const makeRequiredIfBinsAdded = (schema: BaseSchema) => {
  return schema.when('binsPicked', {
    is: (binsPicked: number | null) => binsPicked !== null,
    then: schema => schema.required(Constants.errorMessages.FIELD_REQUIRED),
    otherwise: schema => schema,
  });
};

const harvestDataSchema = yup.object({
  contractor: dropdownRequired,
  startTime: makeRequiredIfBinsAdded(dateSchema),
  headCount: makeRequiredIfBinsAdded(nullableNumberSchema),
  timeWorked: yup
    .object({
      hours: nullableNumberSchema,
      minutes: nullableNumberSchema,
    })
    .when('binsPicked', {
      is: (binsPicked: number | null) => binsPicked !== null,
      then: schema =>
        schema.test('time-required', 'Both fields cannot be 0.', fields => {
          if (!!fields.hours || !!fields.minutes) return true;
          return false;
        }),
      otherwise: schema => schema,
    }),
  transferTime: yup
    .object({
      hours: nullableNumberSchema,
      minutes: nullableNumberSchema,
    })
    .nullable(),
  pickRate: twoDecimalNumbersSchema
    .required(Constants.errorMessages.FIELD_REQUIRED)
    .when('$pickRateMin', (pickRateMin: number | undefined, schema) => {
      if (pickRateMin && pickRateMin > 0) {
        return schema.moreThan(
          pickRateMin,
          `Field must be greater than ${pickRateMin}.`,
        );
      }
      return schema;
    })
    .when('$pickRateMax', (pickRateMax: number | undefined, schema) => {
      if (pickRateMax && pickRateMax > 0) {
        return schema.lessThan(
          pickRateMax,
          `Field must be less than ${pickRateMax}.`,
        );
      }
      return schema;
    })
    .typeError(Constants.errorMessages.NUMBER_REQUIRED),

  binsPicked: yup.number().when('$pickExists', {
    is: true,
    then: () =>
      twoDecimalNumbersSchema
        .required(Constants.errorMessages.FIELD_REQUIRED)
        .typeError(Constants.errorMessages.NUMBER_REQUIRED),
    otherwise: () => twoDecimalNullableSchema,
  }),

  binsHauled: twoDecimalNumbersSchema
    .test(
      'required-if-tarps-or-haul-rate-exists',
      'Required if tarps or haul rate entered.',
      (val, context) => {
        if (!val && (context.parent.tarps || context.parent.haulRate)) {
          return false;
        }
        return true;
      },
    )
    .transform((_, val) => (!val ? null : Number(val)))
    .typeError(Constants.errorMessages.MUST_BE_A_NUMBER),

  tarps: tarpsToBinsHauledSchema
    .test(
      requiredIfBinsExistTest.name,
      requiredIfBinsExistTest.message,
      (val, context) => {
        if (!val && context.parent[`${requiredIfBinsExistTest.testField}`]) {
          return false;
        }
        return true;
      },
    )
    .transform((_, val) => (!val ? null : Number(val)))
    .typeError(Constants.errorMessages.MUST_BE_A_NUMBER),

  haulRate: dropdownOptional.test(
    requiredIfBinsExistTest.name,
    requiredIfBinsExistTest.message,
    (val, context) => {
      if (!val && context.parent[`${requiredIfBinsExistTest.testField}`]) {
        return false;
      }
      return true;
    },
  ),
});

const haulDataSchema = yup.object({
  hauler: dropdownRequired,
  binsHauled: twoDecimalNumbersSchema
    .required(Constants.errorMessages.FIELD_REQUIRED)
    .typeError(Constants.errorMessages.NUMBER_REQUIRED),
  tarps: tarpsToBinsHauledSchema
    .required(Constants.errorMessages.FIELD_REQUIRED)
    .typeError(Constants.errorMessages.NUMBER_REQUIRED),
  haulRate: dropdownRequired,
});

export const pickDataSchema = yup.object({
  harvestDataRecords: yup.array().of(harvestDataSchema).min(1, reqErrorStr),
  haulDataRecords: yup.array().of(haulDataSchema),
  binsLeft: yup.number().when('$binsEntered', {
    is: true,
    then: schema =>
      schema
        .nullable()
        .required(Constants.errorMessages.FIELD_REQUIRED)
        .moreThan(-1, Constants.errorMessages.ZERO_OR_GREATER)
        .integer(Constants.errorMessages.INTEGER_REQUIRED)
        .typeError(Constants.errorMessages.NUMBER_REQUIRED),
    otherwise: () => nullableNumberSchema,
  }),
  fbPerTree: yup.number().when('$binsEntered', {
    is: true,
    then: () => numberSchemaRequired,
    otherwise: () => nullableNumberSchema,
  }),
  notes: nullableStringSchema,
});
